// Catalog.ID Client — Voucher Codes: minting, normalisation, derivation
// ============================================================================
// A registration voucher code, `RGV-XXXX-XXXX-XXXX-XXXX`, and everything that
// is derived from it. Round 10 (registration-wallets §4.1, D16, D25) turned
// this module from a formatting mirror into the source of the codes themselves:
//
//   BEFORE  the server minted 30-character codes under `Voucher:DerivationKey`,
//           received them in plaintext at `register/begin`, at `POST
//           /api/invitation` and INSIDE the signed delegate document it stores
//           verbatim, and hashed them into `voucher.codehash`.
//   NOW     the client mints 16-character codes (80 bits of CSPRNG, the INV
//           rule), derives a LOOKUP ID from each, and the server sees only the
//           lookup id and stores only its hash. No derivation key exists any
//           more; no server can produce, re-derive or recognise a code.
//
// That matters beyond privacy: the code's third derivative is an Ed25519
// keypair whose public half is the CONDITION on the Worker lock holding the
// voucher's bits (§4.2). A server able to derive codes could derive lock keys
// and open the locks. So the derivation stays here, the code never leaves the
// client, and a lost code is lost bits until the lock expires (D26).
//
// Wire contract, shared with the C# `VoucherCodeService` (shape only — the
// server has no derivation), the Worker (public keys and lock keys) and the
// mint tool:
//
//   code          RGV- + 16 characters of the Crockford Base32 alphabet below
//   normalisation upper-case, strip separators, fold I/L to 1 and O to 0. The
//                 prefix's own letters survive the fold untouched, so — unlike
//                 `normaliseInvitationCode`, whose `INV` contains an I — the
//                 fold may run first.
//   derivation    dk = scrypt(UTF-8(16 body chars, prefix excluded),
//                             UTF-8("catalog-id voucher v1"),
//                             N = 2^15, r = 8, p = 1, 96 bytes)
//                 lookup id    = hex_lower(dk[0:32])   ← the only thing on the wire
//                 dk[32:64]      reserved, unused — the layout mirrors the
//                                invitation's so one implementation serves both
//                 Ed25519 seed = dk[64:96]             ← opens the lock
//   codehash      hex_lower(SHA-256(UTF-8(lookup-id HEX STRING))) ← the stored row
//
// Vectors in registration-wallets §4.15, pinned by the sibling test.

import { sha256 } from '@noble/hashes/sha2.js';
import { scryptAsync } from '@noble/hashes/scrypt.js';
import { ed25519 } from '@noble/curves/ed25519.js';
import { encodeUtf8, toHex } from '../crypto/aes';
import { entryUrlForCode } from './invitation-document';

const PREFIX = 'RGV-';

/** Characters after the prefix: 16 × 5 bits = 80 bits (D25; was 30). */
export const VOUCHER_CODE_BODY_LENGTH = 16;

/** Crockford Base32: digits plus A–Z minus I, L, O (folded on input) and U.
 *  Byte-identical to the invitation alphabet and to the server's. */
export const VOUCHER_CODE_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

/**
 * Domain-separation salt for the code KDF. CONSTANT, and DIFFERENT from the
 * invitation's: the same 16 characters typed as a voucher and as an invitation
 * must derive different lookup ids and different keys, or one code would open
 * the other's lock.
 */
export const VOUCHER_KDF_DOMAIN = 'catalog-id voucher v1';

/** scrypt cost — the invitation's, unchanged: 2^15 × 8 × 128 B = 32 MiB and
 *  ~0.1 s, which is what makes a database dump useless for scanning hashes. */
export const VOUCHER_KDF_N = 1 << 15;
export const VOUCHER_KDF_R = 8;
export const VOUCHER_KDF_P = 1;
const VOUCHER_KDF_DKLEN = 96;

/** A lookup id on the wire: the 32 derived bytes as 64 LOWERCASE hex. */
const LOOKUP_ID_RE = /^[0-9a-f]{64}$/;

/**
 * Fold a typed code to its canonical form, or null if it is not a well-formed
 * voucher code. Case-insensitive, tolerant of the spaces and dashes people add
 * when transcribing, and folds the Crockford look-alikes (I/L → 1, O → 0).
 *
 * The 30-character body is GONE and is refused (D25): nothing was live, so
 * there is no old code to keep working, and accepting one would let a stale
 * fixture derive a lookup id no row will ever carry.
 */
export function normalizeVoucherCode(raw: string | null | undefined): string | null {
    if (!raw || raw.length > 128) return null;

    let compact = '';
    for (const ch of raw) {
        if (ch === ' ' || ch === '-' || ch === '_' || ch === '\t' || ch === '\r' || ch === '\n') continue;
        const c = ch.toUpperCase();
        compact += c === 'I' || c === 'L' ? '1' : c === 'O' ? '0' : c;
    }

    // The prefix's own letters must survive the fold untouched, so strip it
    // before validating the body against the alphabet.
    if (!compact.startsWith('RGV')) return null;
    const body = compact.slice(3);
    if (body.length !== VOUCHER_CODE_BODY_LENGTH) return null;
    for (const c of body) {
        if (!VOUCHER_CODE_ALPHABET.includes(c)) return null;
    }
    return PREFIX + body;
}

/** True when `raw` normalises to a well-formed code. */
export function isVoucherCode(raw: string | null | undefined): boolean {
    return normalizeVoucherCode(raw) !== null;
}

/** What a field asking for a code shows while empty: the printed form, so the
 *  placeholder and the card agree (voucher-code.test.ts checks it is one). */
export const VOUCHER_CODE_PLACEHOLDER = 'RGV-XXXX-XXXX-XXXX-XXXX';

/**
 * The printed form: `RGV-XXXX-XXXX-XXXX-XXXX`. Groups of four, matching the
 * invitation card — this is read off paper, typed on a phone and dictated over
 * a telephone. Input that does not normalise is returned untouched: never
 * mangle what the user typed.
 */
export function formatVoucherCode(code: string): string {
    const normalized = normalizeVoucherCode(code);
    if (!normalized) return code;
    const body = normalized.slice(PREFIX.length);
    const groups: string[] = [];
    for (let i = 0; i < body.length; i += 4) groups.push(body.slice(i, i + 4));
    return PREFIX + groups.join('-');
}

/**
 * A fresh voucher code in DISPLAY form. 10 CSPRNG bytes packed 5 bits at a time
 * into 16 alphabet characters — the whole 80 bits, unbiased, with no remainder
 * to throw away. The same rule `mintInvitationCode` follows, deliberately: one
 * packing, one place to get it wrong.
 *
 * Minted by the storefront, the organization's Vouchers pane and
 * `cld-cid voucher-mint`. NEVER by the server (D16).
 */
export function mintVoucherCode(): string {
    const entropy = crypto.getRandomValues(new Uint8Array(10));
    let bits = 0;
    let acc = 0;
    let body = '';
    for (const byte of entropy) {
        acc = (acc << 8) | byte;
        bits += 8;
        while (bits >= 5) {
            bits -= 5;
            body += VOUCHER_CODE_ALPHABET[(acc >>> bits) & 0x1f];
        }
    }
    return formatVoucherCode(PREFIX + body);
}

/** What one voucher code unlocks, and the ONLY things derived from it. */
export interface VoucherSecrets {
    /** 64 lowercase hex characters. What `register/begin`, `POST /api/invitation`
     *  and the v3 delegate document carry; the node stores only
     *  {@link voucherCodeHash} of it. */
    lookupIdHex: string;
    /** 32 raw bytes — the Ed25519 seed that signs a release of this voucher's
     *  locks (§4.2). Never sent anywhere; whoever holds it holds the bits. */
    seed: Uint8Array;
    /** The seed's 32-byte public half: the lock's CONDITION, and the input to
     *  {@link import('../crypto/bitcash').lockKeyFor}. */
    publicKey: Uint8Array;
    /** {@link publicKey} as 64 lowercase hex — the Worker's wire form. */
    publicKeyHex: string;
}

/**
 * The slow half of the contract: one scrypt over the code's 16 body characters
 * under the voucher domain salt, split into a lookup id and an Ed25519 seed.
 *
 * The PREFIX is excluded from the KDF input — it routes the entry page, carries
 * no entropy, and would only be one more thing for three implementations to
 * disagree about.
 *
 * Async (`scryptAsync`) so a 32 MiB, ~0.1 s derivation does not freeze the tab.
 */
export async function deriveVoucherSecrets(code: string): Promise<VoucherSecrets> {
    const normalised = normalizeVoucherCode(code);
    if (!normalised) throw new Error('That is not a valid voucher code');
    const body = normalised.slice(PREFIX.length);
    const dk = await scryptAsync(encodeUtf8(body), encodeUtf8(VOUCHER_KDF_DOMAIN), {
        N: VOUCHER_KDF_N, r: VOUCHER_KDF_R, p: VOUCHER_KDF_P, dkLen: VOUCHER_KDF_DKLEN,
    });
    // `slice`, not `subarray`: the seed must not be a view onto the whole
    // derived key, or a caller that zeroes it would blank the lookup id too.
    const seed = dk.slice(64, 96);
    const publicKey = ed25519.getPublicKey(seed);
    return {
        lookupIdHex: toHex(dk.subarray(0, 32)),
        seed,
        publicKey,
        publicKeyHex: toHex(publicKey),
    };
}

/**
 * What the node stores as `voucher.codehash`: SHA-256 over the lookup id's 64
 * HEX CHARACTERS (their ASCII bytes), not over the 32 bytes they encode — the
 * `secrethash` rule of §14.2, applied to vouchers. Mirrored in C#; the one
 * place two implementations would silently diverge, so both sides pin it.
 *
 * Uppercase input is refused rather than folded: the preimage is the string,
 * and the server's shape check refuses uppercase too.
 */
export function voucherCodeHash(lookupIdHex: string): string {
    if (!LOOKUP_ID_RE.test(lookupIdHex)) {
        throw new Error('A voucher lookup id is 64 lowercase hex characters');
    }
    return toHex(sha256(encodeUtf8(lookupIdHex)));
}

/**
 * The entry link a voucher card's QR carries: `https://<site>/register#code=RGV-…`
 * and nothing else (§7). The entry page routes on the prefix, so this is the
 * invitation card's URL shape with a voucher code in it.
 */
export function voucherUrl(site: string, code: string): string {
    const normalised = normalizeVoucherCode(code);
    if (!normalised) throw new Error('That is not a valid voucher code');
    return entryUrlForCode(site, formatVoucherCode(normalised));
}
